import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
    LayoutDashboard,
    FlaskConical,
    Activity,
    Waves,
    MapPinned,
    BellRing,
    Droplets,
    Calculator,
    Beaker,
    PackageCheck,
    Sliders,
    ArrowLeftRight,
    Layers,
    FileText,
    Sigma,
    AudioWaveform,
    Combine,
    Radio,
    CalendarDays,
    ArrowUpRight,
} from "lucide-react";
import { ASSETS } from "../lib/constants";

const FEATURES = [
    {
        slug: "daily-dashboard",
        icon: LayoutDashboard,
        t: "Daily Dashboard",
        d: "Your complete schedule at a glance. Checkboxes, visual syringe, and quick injection stacking.",
    },
    {
        slug: "compound-library",
        icon: FlaskConical,
        t: "Compound Library",
        d: "100+ pre-configured compounds across anabolics, TRT ancillaries, peptides, GLPs, SARMs and supplements. One tap to add.",
    },
    {
        slug: "pharmacokinetic-charting",
        icon: Activity,
        t: "Pharmacokinetic Charting",
        d: "A steroid plotter with a true multi-route PK model — injectables, orals, peptides, and testosterone cream.",
    },
    {
        slug: "t-level-fluctuation",
        icon: Waves,
        t: "T Level Fluctuation",
        d: "Peak-to-trough modeling based on your exact protocol. See how levels oscillate — not just the average.",
    },
    {
        slug: "closed-loop-dose-calculator",
        icon: Sigma,
        t: "Closed-Loop Dose Calculator",
        d: "Edit any field and the rest recalculate instantly. Dose, volume, and frequency, always in sync.",
    },
    {
        slug: "frequency-tuning",
        icon: AudioWaveform,
        t: "Frequency Tuning",
        d: "Detects when your peak-to-trough swing is higher than it needs to be. Suggests the most practical frequency to smooth it.",
    },
    {
        slug: "dose-reminders",
        icon: BellRing,
        t: "Dose Reminders",
        d: "Notifications when each compound is due. Mark as taken or snooze from your phone or smartwatch.",
    },
    {
        slug: "rotation-planner",
        icon: MapPinned,
        t: "Rotation Planner",
        d: "Set your preferred injection sites once. The app forecasts your rotation — no more hammering one spot.",
    },
    {
        slug: "live-testosterone-readout",
        icon: Radio,
        t: "Live Testosterone Readout",
        d: "Estimated Total T, live on screen. Calibrated to your own blood test and dose response.",
        linkTo: "/blog/live-testosterone-readout",
        linkLabel: "Read the article",
    },
    {
        slug: "protocol-log",
        icon: CalendarDays,
        t: "Protocol Log",
        d: "A day-by-day record of every dose, check-in, health metric and note, kept automatically as you go.",
    },
    {
        slug: "inventory-management",
        icon: PackageCheck,
        t: "Inventory Management",
        d: "Protocol-aware supply tracking with advance reorder notifications. Know before you're empty.",
    },
    {
        slug: "protocol-summaries",
        icon: FileText,
        t: "Protocol Summaries",
        d: "Clean bro-notation format. Share with your coach, clinic, or training partner in seconds.",
    },
    {
        slug: "classic-advanced-mode",
        icon: Layers,
        t: "Classic & Advanced Mode",
        d: "Classic simple view for core TRT essentials. Advanced unlocks compound stacking for enhanced protocols.",
    },
    {
        slug: "syringe-stacking",
        icon: Combine,
        t: "Syringe Stacking",
        d: "Combine multiple compounds into a single injection. Visual volumes verify each dose. Fewer injections for enhanced protocols.",
    },
    {
        slug: "testosterone-cream",
        icon: Droplets,
        t: "Testosterone Cream",
        d: "Topi-CLICK® interface with Michaelis-Menten saturation kinetics and site-specific absorption.",
    },
    {
        slug: "cream-injectable-converter",
        icon: Calculator,
        t: (
            <>
                Cream{" "}
                <ArrowLeftRight
                    size={16}
                    strokeWidth={2}
                    className="inline align-[-2px] mx-0.5 text-white"
                    aria-label="to and from"
                />{" "}
                Injectable Converter
            </>
        ),
        d: "Weekly cream amount → injectable equivalent in mg/W. Site-specific, clinically meaningful.",
    },
    {
        slug: "peptide-reconstitution",
        icon: Beaker,
        t: "Peptide Reconstitution",
        d: "Enter dry powder amount — optimal bac water volume calculates automatically. Override either direction.",
    },
    {
        slug: "dose-titration",
        icon: Sliders,
        t: "Dose Titration",
        d: "Set automatic ramping and tapering plans for any compound.",
    },
];

export default function MyProtocol() {
    return (
        <section
            id="protocol"
            data-testid="protocol-section"
            className="relative py-24 lg:py-32 bg-black overflow-hidden"
        >
            {/* subtle blue wash */}
            <div
                className="absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full pointer-events-none"
                style={{
                    background:
                        "radial-gradient(circle, rgba(43,166,224,0.08), transparent 60%)",
                }}
            />

            <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start mb-14 lg:mb-20">
                    <div>
                        <div className="eyebrow mb-6">
                            <span className="dot" /> My Protocol
                        </div>
                        <h2 className="font-display font-bold clamp-h2 text-white">
                            Your protocol.
                            <br />
                            Every compound.
                            <br />
                            <span className="blue-glow">Set up in a minute.</span>
                        </h2>
                    </div>
                    <p className="text-white/65 text-base lg:text-lg leading-relaxed lg:pt-4">
                        Whether you're running a straightforward TRT protocol
                        or managing a full enhanced stack through to peak
                        conditioning and contest prep — TRT Plus handles the
                        complexity with features built specifically for the
                        job.
                    </p>
                </div>

                <div className="grid lg:grid-cols-[1fr_1.4fr] gap-10 lg:gap-16 items-start">
                    {/* Phone */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 0.8 }}
                        className="relative mx-auto lg:mx-0 w-full max-w-[340px] lg:sticky lg:top-28"
                    >
                        <div className="phone-frame">
                            <div className="phone-screen">
                                <img
                                    src={ASSETS.protocolScreen}
                                    alt="My Protocol"
                                    className="absolute inset-0 w-full h-full object-cover object-top"
                                />
                            </div>
                        </div>
                        {/* Floating chip — Visual syringe */}
                        <div className="absolute -right-4 top-[70%] hidden lg:flex items-center gap-2 bg-black/85 border border-white/10 backdrop-blur-xl rounded-full px-3 py-2 text-xs shadow-lg">
                            <span className="pulse-dot-blue w-2 h-2 rounded-full bg-[#2ba6e0]" />
                            <span className="text-white/80 font-mono">
                                Visual syringe
                            </span>
                        </div>
                    </motion.div>

                    {/* Features grid */}
                    <div className="grid sm:grid-cols-2 gap-4">
                        {FEATURES.map((f, i) => {
                            const content = (
                                <>
                                    {f.linkTo && (
                                        <ArrowUpRight
                                            size={14}
                                            className="protocol-card-corner absolute top-3 right-3 text-white/30 group-hover:text-[#49bef5] transition-colors"
                                            aria-hidden="true"
                                        />
                                    )}
                                    <div className="w-10 h-10 rounded-lg bg-[#2ba6e0]/10 border border-[#2ba6e0]/25 flex items-center justify-center mb-4">
                                        <f.icon
                                            size={18}
                                            className="text-[#49bef5]"
                                        />
                                    </div>
                                    <h3 className="font-display font-semibold text-white text-[17px] mb-1.5">
                                        {f.t}
                                    </h3>
                                    <p className="text-sm text-white/55 leading-relaxed">
                                        {f.d}
                                    </p>
                                    {f.linkTo && (
                                        <div className="mt-4 pt-3 border-t border-white/5 text-[10.5px] font-mono uppercase tracking-[0.18em] flex items-center gap-1.5">
                                            <span className="text-[#00E6A0]">
                                                {">"}
                                            </span>
                                            <span className="text-white/55 group-hover:text-white transition-colors">
                                                {f.linkLabel}
                                            </span>
                                            <span className="text-[#49BEF5]">
                                                →
                                            </span>
                                        </div>
                                    )}
                                </>
                            );
                            const sharedProps = {
                                className: `tile p-5 relative block h-full ${f.linkTo ? "group cursor-pointer hover:border-white/20 transition-colors" : ""}`,
                                "data-testid": `feature-${f.slug}`,
                            };
                            return (
                                <motion.div
                                    key={f.slug}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.2 }}
                                    transition={{ duration: 0.4, delay: (i % 6) * 0.04 }}
                                    className="h-full"
                                >
                                    {f.linkTo ? (
                                        <Link to={f.linkTo} {...sharedProps}>
                                            {content}
                                        </Link>
                                    ) : (
                                        <div {...sharedProps}>{content}</div>
                                    )}
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
}
